import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [proprietairesOpen, setProprietairesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProprietairesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const proprietairesLinks = [
    { href: "/estimation-foret", label: "Estimer ma forêt" },
    { href: "/vendre-parcelle-bois", label: "Vendre ma forêt" },
    { href: "/vendre-bois-sur-pied", label: "Valoriser mes bois" },
    { href: "/successions-forestieres", label: "Succession / Indivision" }
  ];

  const navLinks = [
    { href: "/#services", label: "Services" },
    { href: "/#carbone", label: "Carbone" },
    { href: "/#approach", label: "Approche" },
    { href: "/#who-we-help", label: "Clients" },
    { href: "/#about", label: "À propos" },
    { href: "/ressources", label: "Ressources", isPage: true },
    { href: "/#contact", label: "Contact" },
  ];

  const handleNavClick = (href, isPage = false) => {
    setIsOpen(false);
    setProprietairesOpen(false);
    
    if (isPage) {
      navigate(href);
      return;
    }
    
    // If we're on the homepage, scroll to section
    if (isHomePage) {
      const sectionId = href.replace("/#", "#");
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // Navigate to homepage with hash
      navigate(href);
    }
  };

  return (
    <>
      <nav
        data-testid="navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || !isHomePage ? "bg-cream/95 backdrop-blur-md shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Brand Name */}
            <Link to="/" className="flex items-center" data-testid="logo">
              <span className={`font-serif text-2xl font-semibold tracking-tight ${
                scrolled || !isHomePage ? "text-forest" : "text-white"
              }`}>Sylva Invest</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {/* Propriétaires Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setProprietairesOpen(!proprietairesOpen)}
                  className={`flex items-center text-sm font-medium tracking-wide whitespace-nowrap transition-colors ${
                    scrolled || !isHomePage 
                      ? "text-forest hover:text-gold" 
                      : "text-white/90 hover:text-gold"
                  }`}
                  data-testid="nav-proprietaires"
                >
                  Propriétaires
                  <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${proprietairesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {proprietairesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-56 bg-white shadow-lg border border-gray-100 py-2"
                    >
                      {proprietairesLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          onClick={() => setProprietairesOpen(false)}
                          className="block px-4 py-2 text-sm text-forest hover:bg-bone hover:text-gold transition-colors"
                          data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-').replace('/', '-')}`}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href, link.isPage)}
                  className={`text-sm font-medium tracking-wide whitespace-nowrap transition-colors ${
                    scrolled || !isHomePage 
                      ? "text-forest hover:text-gold" 
                      : "text-white/90 hover:text-gold"
                  } ${link.label === "Ressources" ? "text-gold" : ""}`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => handleNavClick("/#contact")}
                className="btn-primary whitespace-nowrap"
                data-testid="nav-cta-button"
              >
                Nous contacter
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`lg:hidden p-2 ${scrolled || !isHomePage ? "text-forest" : "text-white"}`}
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-0 z-40 bg-cream lg:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center pt-24 pb-12 px-6 space-y-6">
              {/* Propriétaires Section */}
              <div className="text-center">
                <p className="text-xs uppercase tracking-[0.2em] text-gold font-sans font-semibold mb-4">Propriétaires</p>
                <div className="space-y-3">
                  {proprietairesLinks.map((link, index) => (
                    <motion.div
                      key={link.href}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={link.href}
                        onClick={() => setIsOpen(false)}
                        className="block text-lg font-serif text-forest hover:text-gold transition-colors"
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="w-12 h-px bg-gray-200" />

              {/* Main Nav Links */}
              {navLinks.map((link, index) => (
                <motion.button
                  key={link.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.05 }}
                  onClick={() => handleNavClick(link.href, link.isPage)}
                  className={`text-xl font-serif text-forest ${link.label === "Ressources" ? "text-gold" : ""}`}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </motion.button>
              ))}
              
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                onClick={() => handleNavClick("/#contact")}
                className="btn-primary mt-6"
                data-testid="mobile-cta-button"
              >
                Nous contacter
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
